import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/Key";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MuiListItem from "@mui/material/ListItem";
import { styled, useTheme } from "@mui/material/styles";
import { useGoogleLogin } from "@react-oauth/google";
import { AuthenticationContext } from "components/context/AuthenticationProvider";
import { SnackbarContext } from "components/context/SnackbarProvider";
import { UserContext } from "components/context/UserProvider";
import { ViewCheckbox } from "components/ui/atoms/Checkbox";
import Highlight from "components/ui/atoms/Highlight";
import Layout from "components/ui/molecules/Layout";
import {
  TopRightMenu,
  TopRightMenuItem,
} from "components/ui/molecules/TopRightMenu";
import {
  useAllAccount,
  useGenerateLongLiveToken,
  useUpdateAccount,
} from "models/accounts/apiAccount";
import dayjs from "dayjs";
import { useContext, useState } from "react";

const StyledColumnTitle = styled(Grid)(
  ({ theme }) => `
  font-weight: ${theme.fontWeight[400]};
`,
);

const StyledLongText = styled(Grid)(
  () => `
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`,
);

const SCOPES =
  "https://www.googleapis.com/auth/calendar.events.readonly https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/drive.metadata.readonly https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid";
const SCOPES_SERVICE =
  "https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.scripts https://www.googleapis.com/auth/script.external_request https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid";

const Account = ({}) => {
  const theme = useTheme();
  const { googleToken } = useContext(AuthenticationContext);
  const { hasPermission } = useContext(UserContext);
  const { displayMessage, displayError } = useContext(SnackbarContext);
  const { mutateAsync: mutateAccountsAsync } = useUpdateAccount();
  const { mutateAsync: generateTokenAsync } = useGenerateLongLiveToken();
  const {
    data: accounts,
    isLoading: accountsIsLoading,
    refetch: refetchAccount,
  } = useAllAccount();

  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);

  const defaultGoogleLoginConfig = {
    flow: "auth-code",
    prompt: "none",
    overrideScope: true,
    include_granted_scopes: false,
    access_type: "offline",
    onError: displayError,
    onNonOAuthError: displayError,
  };

  const saveAccount = (tokenResponse) => {
    return mutateAccountsAsync({
      credentials: tokenResponse,
      is_service: false,
    })
      .then(() => {
        refetchAccount();
        displayMessage("Account is saved.", <>&#127881;</>, "success");
      })
      .catch(displayError);
  };

  const login = useGoogleLogin({
    ...defaultGoogleLoginConfig,
    onSuccess: saveAccount,
    onError: displayError,
    scope: SCOPES,
  });

  // Google only returns a refresh token when the consent screen is shown:
  // without it, the restored credentials would expire again within the hour.
  const reconnect = useGoogleLogin({
    ...defaultGoogleLoginConfig,
    prompt: "consent",
    onSuccess: saveAccount,
    onError: displayError,
    scope: SCOPES,
  });

  const loginService = useGoogleLogin({
    ...defaultGoogleLoginConfig,
    onSuccess: (tokenResponse) => {
      return mutateAccountsAsync({
        credentials: tokenResponse,
        is_service: true,
      })
        .then(() => {
          refetchAccount();
          displayMessage(
            "Service Account is saved.",
            <>&#127881;</>,
            "success",
          );
        })
        .catch(displayError);
    },
    scope: SCOPES_SERVICE,
  });

  const handleGenerateToken = async () => {
    try {
      const tokenData = await generateTokenAsync();
      setGeneratedToken(tokenData);
      setTokenDialogOpen(true);
      displayMessage(
        "Token generated successfully!",
        <>&#127881;</>,
        "success",
      );
    } catch (error) {
      displayError(error);
    }
  };

  const handleCloseDialog = () => {
    setTokenDialogOpen(false);
    setGeneratedToken(null);
  };

  const handleCopyToken = () => {
    if (generatedToken?.token) {
      navigator.clipboard.writeText(generatedToken.token);
      displayMessage("Token copied to clipboard!", null, "success");
    }
  };

  return (
    <Layout container hideBreadcrumbs>
      <Layout title color={theme.palette_custom.lightGreen} variant="component">
        <div>
          <Highlight color={theme.palette_custom.lightGreen}>Account</Highlight>
        </div>
        <TopRightMenu style={{ marginLeft: "auto" }}>
          <TopRightMenuItem
            alwaysShow
            onClick={login}
            endIcon={<AddIcon sx={{ scale: "0.8" }} />}
          >
            Update/Create Account
          </TopRightMenuItem>
          {hasPermission("app.can_add_service_account") && (
            <TopRightMenuItem neverShow onClick={loginService}>
              Update/Create Service Account
            </TopRightMenuItem>
          )}
          {hasPermission("app.can_add_service_account") && (
            <TopRightMenuItem
              alwaysShow
              onClick={handleGenerateToken}
              endIcon={<KeyIcon sx={{ scale: "0.8" }} />}
            >
              Long live token
            </TopRightMenuItem>
          )}
        </TopRightMenu>
      </Layout>
      <Layout content>
        {accountsIsLoading && (
          <Stack spacing={1}>
            <Skeleton variant="text" />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
          </Stack>
        )}
        {!accountsIsLoading && accounts?.length == 0 && <>Empty</>}
        {!accountsIsLoading && accounts?.length > 0 && (
          <List>
            <MuiListItem key="title">
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ width: "100%" }}
              >
                <StyledColumnTitle size={{ md: 1, xs: 2 }}>
                  Image
                </StyledColumnTitle>
                <StyledColumnTitle size={{ md: 1, xs: 2 }}>
                  Id
                </StyledColumnTitle>
                <StyledColumnTitle
                  item
                  size={{ md: 3 }}
                  display={{ xs: "none", md: "block" }}
                  xs={8}
                >
                  Username
                </StyledColumnTitle>
                <StyledColumnTitle
                  item
                  size={{ md: 3 }}
                  display={{ xs: "none", md: "block" }}
                >
                  Name
                </StyledColumnTitle>
                <StyledColumnTitle size={{ md: 3, xs: 6 }}>
                  Email_address
                </StyledColumnTitle>
                <StyledColumnTitle size={{ md: 1, xs: 2 }}>
                  Service
                </StyledColumnTitle>
              </Grid>
            </MuiListItem>
            {accounts.map((account, index) => (
              <MuiListItem
                key={index}
                sx={{
                  border: "0.5px solid black",
                  margin: "10px 0",
                }}
              >
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Grid size={{ md: 1, xs: 2 }}>
                    <Avatar alt={account.name} src={account.image_url} />
                  </Grid>
                  <Grid size={{ md: 1, xs: 2 }}>{account.id}</Grid>
                  <Grid size={{ md: 3 }} display={{ xs: "none", md: "block" }}>
                    {account.user.username}
                  </Grid>
                  <Grid size={{ md: 3 }} display={{ xs: "none", md: "block" }}>
                    {account.name}
                  </Grid>
                  <Grid size={{ md: 3, xs: 6 }}>
                    <StyledLongText>{account.email_address}</StyledLongText>
                    {account.credentials_invalid_since && (
                      <Tooltip
                        title={`Google access lost on ${dayjs(
                          account.credentials_invalid_since,
                        ).format(
                          "DD/MM/YYYY",
                        )}: emails, contacts and calendar are no longer imported.`}
                      >
                        <Chip
                          size="small"
                          color="warning"
                          icon={<SyncProblemIcon />}
                          label="Reconnect"
                          onClick={reconnect}
                        />
                      </Tooltip>
                    )}
                  </Grid>
                  <Grid size={{ md: 1, xs: 2 }}>
                    <ViewCheckbox value={account.is_service} />
                  </Grid>
                </Grid>
              </MuiListItem>
            ))}
          </List>
        )}
      </Layout>
      <Layout content>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ wordBreak: "break-all" }}
        >
          Google Token: {googleToken || "No token available"}
        </Typography>
      </Layout>

      <Dialog
        open={tokenDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Long Live Token (1 Month)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This token will expire in {generatedToken?.expires_in_days || 30}{" "}
            days. Use it for API authentication. Keep it secure and do not share
            it publicly.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={generatedToken?.token || ""}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title="Copy to clipboard">
                  <IconButton onClick={handleCopyToken} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            sx={{ fontFamily: "monospace" }}
          />
          {generatedToken?.user && (
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              Generated for user: <strong>{generatedToken.user}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyToken} startIcon={<ContentCopyIcon />}>
            Copy Token
          </Button>
          <Button onClick={handleCloseDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Account;
